import React, {
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  ReactNode,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation, Route } from "react-router-dom";
import axios from "axios";

import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SettingsContext, SettingsTab } from "src/app/SettingsModal/enums";
import { ExtensionsSection } from "src/app/SettingsModal/tabs/Extensions/Extensions";
import {
  openSettingsModal,
  removePropertyFromViewParam,
} from "src/app/SettingsModal/methods";
import { HowItWorksModal } from "src/app/SettingsModal/tabs/Extensions/components/RecruitmentForm/components/HowItWorksModal/HowItWorksModal";
import { getCouponsList } from "src/redux/coupon/couponActions";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { AppLocation } from "src/redux/main/mainReducer";
import { RootState } from "src/redux/reducers";
import CreatePaymentModal from "src/app/modals/CreatePaymentModal/CreatePaymentModal";
import { uuidv7 } from "uuidv7";
import { useCheckFeatureAccess } from "src/app/subscription/subscriptionFunctions";
import { SubscriptionEntitlements } from "src/app/subscription/subscriptionUtils";
import {
  closeAddCreatorsModal,
  closeAddPublicationModal,
  closeAddTaskModal,
  getProject,
  setSelectedTasks,
  setTasksLoading,
  setTasksList,
  clearTaskList,
  clearExtensionList,
  setCreateSingleSplitModalOpen,
  getProjectTasksAutocomplete,
  setSelectedTaskColumns,
} from "src/redux";
import { TaskPane } from "../../../Task/Task";
import AddTaskModal from "../../../modals/AddTaskModal";
import Board from "../../../board/Board";
import { IColumn } from "../../Project";
import ShareProjectOrTaskModal from "../../../modals/ShareProjectOrTaskModal/ShareProjectOrTaskModal";
import { API_URLS } from "../../../../utils/API_URLS";
import AddCreatorsModal from "../../../modals/AddCreatorsModal/AddCreatorsModal";
import {
  capitalizeFirstLetter,
  getDictionaryAutoCompletesForColumns,
  setQueryParam,
  getSelectDataSetForColumns,
  sleep,
} from "../../../../utils/methods";
import ManageFieldModal from "../../../modals/ManageFieldModal/ManageFieldModal";
import { TaskType, type ColumnOption } from "src/types";
import { Metadata } from "../../../Task/Subtask/Subtask";
import { tableDataType } from "../../../components/Table/Table";
import TasksTableWrapper from "./TasksTableWrapper";
import AddPublicationModal from "../../../modals/AddPublicationModal";
import DeleteMetaFieldModal from "../../../modals/DeleteMetaFieldModal/DeleteMetaFieldModal";
import { EmptyTable } from "./EmptyTable";
import { cancelRequestAxios } from "../../../methods/CancelRequest";
import { SingleProjectSelectedTasksBar } from "../components/SingleProjectSelectedTasksBar/SingleProjectSelectedTasksBar";
import { ifRowMatchesFilters } from "./tasksTableSorting/tableSortingFunctions/filterTableData";
import { createProjectListTableView } from "../../../TableView/tableViewFunctions";
import { SaveTableViewModal } from "../../../TableView/components/SaveTableViewModal/SaveTableViewModal";
import { CurrentViewValue } from "../types";
import SingleProjectToolbar from "../components/SingleProjectToolbar";
import NoListResults from "../components/NoListResults";
import TaskTablePanes from "./components/TaskTablePanes";

const BOARD_META_FIELDS_WHITELIST = [
  MetaFieldType.Member,
  MetaFieldType.Creator,
  MetaFieldType.Content,
  MetaFieldType.SingleSelect,
  MetaFieldType.Date,
  MetaFieldType.BoolVal,
];

const cancelRequestAxiosObject = new cancelRequestAxios();

interface TasksTableViewProps {
  currentView: CurrentViewValue;
  setCurrentView: Dispatch<SetStateAction<CurrentViewValue>>;
  boardHidden: boolean;
  setBoardHidden: Dispatch<SetStateAction<boolean>>;
  renderTopBar: (children: ReactNode) => ReactNode;
}

export default function TasksTableView({
  currentView,
  setCurrentView,
  boardHidden,
  setBoardHidden,
  renderTopBar,
}: TasksTableViewProps) {
  const [editedFieldData, setEditedFieldData] = useState<any>({});
  const [showAddTaskFieldModal, setShowAddTaskFieldModal] =
    useState<boolean>(false);

  const [showAccessLinkModal, setShowAccessLinkModal] =
    useState<boolean>(false);

  const [showTaskFieldSetupModal, setShowTaskFieldSetupModal] = useState(false);

  const [taskFields, setTaskFields] = useState<IColumn[]>([]);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [sortingColumn, setSortingColumn] = useState<ColumnOption | null>(null);
  const [columnOptions, setColumnOptions] = useState<ColumnOption[]>([]);
  const [showDeleteMetaFieldModal, setShowDeleteMetaFieldModal] =
    useState(false);
  const [showSaveTableViewModal, setShowSaveTableViewModal] = useState(false);
  const [newTableViewName, setNewTableViewName] = useState("");
  // const [selectedWsTableView, setSelectedWsTableView] = useState({
  //   label: "All",
  //   value: null,
  // });

  const history = useHistory();

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const howItWorksModalData = useSelector(
    (state: RootState) => state.mainReducer.howItWorksModalData,
  );

  const identity = useSelector(
    (state: RootState) => state.mainReducer.identity,
  );

  const taskType = useSelector(
    (state: RootState) => state.projectReducer.taskType,
  );

  const projectMetaFields = useSelector(
    (state: RootState) => state.projectReducer.projectMetaFields,
  );

  const projectBasicData = useSelector(
    (state: RootState) => state.projectReducer.projectBasicData,
  );

  const tasks = useSelector((state: RootState) => state.taskReducer.tasks);

  const tasksColumns = useSelector(
    (state: RootState) => state.taskReducer.tasksColumns,
  );

  const tasksLoading = useSelector(
    (state: RootState) => state.taskReducer.tasksLoading,
  );

  const showAddTaskModal = useSelector(
    (state: RootState) => state.taskReducer.showAddTaskModal,
  );

  const showAddCreatorsModal = useSelector(
    (state: RootState) => state.taskReducer.showAddCreatorsModal,
  );

  const showAddPublicationModal = useSelector(
    (state: RootState) => state.taskReducer.showAddPublicationModal,
  );

  const selectedFilters = useSelector(
    (state: RootState) => state.taskFiltersReducer.selectedFilters,
  );

  const selectedTasks = useSelector(
    (state: RootState) => state.taskReducer.selectedTasks,
  );

  const { isCreateSingleSplitPaymentsModalOpen } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const params = useParams<{
    projectId: string;
    taskId: string;
    commentId: string;
  }>();

  const { projectId } = params;

  const dispatch = useDispatch();
  const { search } = useLocation();

  const hasCouponsAccess = useCheckFeatureAccess(
    SubscriptionEntitlements.CouponsIntegration,
  );

  const lastTaskRank = tasks?.length ? tasks[tasks.length - 1].rank : "";
  const isAutoIncrementPaymentTitlesExtensionEnabled =
    Boolean(identity?.wsExtensionAutoIncrementPaymentTitles?.length) &&
    taskType === TaskType.Payment;

  useEffect(() => {
    const id = setTimeout(() => {
      setBoardHidden(false);
    }, 150);
    return () => clearTimeout(id);
  }, [tasks]);

  useEffect(() => {
    dispatch(setSelectedTasks([]));
    dispatch(setSelectedTaskColumns([]));
  }, [taskType, dispatch]);

  useEffect(() => {
    let projectFields = [];
    if (tasksColumns.length) {
      projectFields = tasksColumns
        .sort((a, b) => a.metaFieldRank?.localeCompare(b.metaFieldRank))
        .map((field) => ({
          id: field.metaFieldId,
          name: field.metaFieldName,
          type: field.metaFieldType,
          isVisible: field.metaFieldVisible,
          isRequired: field.metaFieldIsRequired,
          rank: field.metaFieldRank,
          key: field.metaFieldKey,
          valueSource: field.metaFieldValueSource,
          metaFieldOptions: field.data,
          metaFieldSource: field.metaFieldSource,
        }));
      setTaskFields(projectFields);
    }
  }, [tasksColumns, taskType]);

  useEffect(() => {
    if (!tasks?.length) return;
    const columnNames: ColumnOption[] = [];
    const tasksMetaData = tasks[0]?.metadata;

    if (tasksColumns.length) {
      tasksColumns
        .filter((field) =>
          tasksMetaData?.find(
            (metafield) =>
              metafield.uuid === field.metaFieldId &&
              BOARD_META_FIELDS_WHITELIST.includes(metafield.type),
          ),
        )
        .forEach((column) =>
          columnNames.push({
            uuid: column.metaFieldId,
            label: column.metaFieldName,
            value: column.metaFieldName,
            type: column.metaFieldType,
          }),
        );
    }
    setColumnOptions(columnNames);
  }, [tasksColumns, taskType]);

  useEffect(() => {
    const findOption = columnOptions?.find(
      (option) => option.uuid === sortingColumn?.uuid,
    );

    if ((!sortingColumn || !findOption) && columnOptions.length > 0) {
      setSortingColumn(columnOptions[0]);
    }
  }, [columnOptions]);

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const paramTaskType = searchParams.get("taskType");
    const paramTab = searchParams.get("tab");

    if (paramTaskType || paramTab === "creatorSearch") return;
    if (taskType) {
      setQueryParam(history, "taskType", taskType);
    } else {
      setQueryParam(history, "taskType", TaskType.Creator);
    }
  }, [search, taskType, history]);

  useEffect(() => {
    const getNewTasksList = async () => {
      try {
        await sleep(15);
        dispatch(setTasksLoading(true));

        const url = `${API_URLS.getTasksList.replace(
          ":projectUuid:",
          projectId,
        )}`;

        cancelRequestAxiosObject.cancelAndCreateToken();

        const cancelToken =
          await cancelRequestAxiosObject?.cancel_request?.token;

        const response = await axios.get(url, {
          params: {
            wsTaskType: taskType,
          },
          method: "GET",
          ...(cancelToken && { cancelToken }),
        });
        cancelRequestAxiosObject.resetCancelToken();

        const {
          tasks: newTasks,
          columns: newTasksColumns,
          titleMetaFieldId,
          totalCount,
        } = response.data.content;

        await dispatch(
          setTasksList({
            tasks: newTasks,
            tasksColumns: newTasksColumns,
            titleMetaFieldId,
            totalCount,
          }),
        );
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(setTasksLoading(false));
      }
    };
    getNewTasksList();
  }, [taskType, currentView, projectId, dispatch]);

  useEffect(() => {
    dispatch(getProjectTasksAutocomplete(projectId));
  }, [projectId, activeWorkspaceUuid, dispatch]);

  useEffect(() => {
    if (!hasCouponsAccess || !tasksColumns) return;

    const couponField = tasksColumns.find(
      (item) => item.metaFieldType === "coupon",
    );

    if (couponField) {
      dispatch(getCouponsList(activeWorkspaceUuid));
    }
  }, [tasksColumns, hasCouponsAccess, activeWorkspaceUuid, dispatch]);

  useEffect(() => {
    if (tasksColumns.findIndex((taskColumn) => taskColumn.isMocked) !== -1)
      return;

    const taskColumns = tasksColumns.map((column) => ({
      type: column.metaFieldType,
      options: column.data,
    }));

    const projectColumns = projectMetaFields.map((column) => ({
      type: column.type,
      options: column.data,
    }));

    const columns = [...taskColumns, ...projectColumns];

    getDictionaryAutoCompletesForColumns(dispatch, columns);

    getSelectDataSetForColumns(dispatch, columns);
  }, [tasksColumns, projectMetaFields, dispatch]);

  useEffect(() => {
    dispatch(clearTaskList());
    dispatch(clearExtensionList());
  }, [dispatch]);

  if (!projectBasicData) {
    return null;
  }

  const filteredTasksCount = tasks.reduce(
    (acc, task) =>
      acc + Number(ifRowMatchesFilters(task, selectedFilters, identity.id)),
    0,
  );

  const saveTableView = async () => {
    // TODO: pass data in proper format (see interface inside )
    await createProjectListTableView({
      filters: {},
      sortBy: null,
      sort: null,
      visibleColumns: [],
      wsWorkspaceUuid: activeWorkspaceUuid,
      wsProjectUuid: projectId,
      name: newTableViewName,
      uuid: uuidv7(),
    });
  };

  return (
    <>
      {renderTopBar(
        <SingleProjectToolbar
          setShowAccessLinkModal={setShowAccessLinkModal}
          setBoardHidden={setBoardHidden}
          setSortingColumn={setSortingColumn}
          setShowAddTaskFieldModal={setShowAddTaskFieldModal}
          setCurrentView={setCurrentView}
          currentView={currentView}
          sortingColumn={sortingColumn}
          columnOptions={columnOptions}
          taskFields={taskFields}
          setShowTaskFieldSetupModal={setShowTaskFieldSetupModal}
          editedFieldData={editedFieldData}
          setEditedFieldData={setEditedFieldData}
        />,
      )}
      {currentView === "list" ? (
        <TasksTableWrapper columns={tasksColumns} />
      ) : null}

      {currentView === "board" ? (
        <Board
          tasks={tasks.map((singleTask) => {
            const { taskId, title, ...taskData } = singleTask;

            return {
              ...taskData,
              projectId: singleTask.taskId,
              projectName: singleTask.title,
              metadata: singleTask.metadata
                // .filter((field: { type: string }) =>
                //   BOARD_META_FIELDS_WHITELIST.includes(field.type)
                // )
                .map((metafield: Metadata) => {
                  if (metafield.type === "date" && metafield.value) {
                    return {
                      ...metafield,
                      value: metafield.value.date,
                    };
                  }

                  return metafield;
                }),
              parentType: "ws_task",
            };
          })}
          fields={taskFields}
          column={sortingColumn}
          hidden={boardHidden}
        />
      ) : null}

      {filteredTasksCount === 0 &&
        currentView === "list" &&
        !tasksLoading &&
        Object.keys(selectedFilters).length !== 0 && <NoListResults />}

      {currentView === "list" &&
        !tasksLoading &&
        tasks.length === 0 &&
        Object.keys(selectedFilters).length === 0 && (
          <EmptyTable taskType={taskType} />
        )}

      {selectedTasks.length > 0 && <SingleProjectSelectedTasksBar />}

      {showAddTaskModal && projectBasicData && (
        <AddTaskModal
          projectId={projectBasicData.projectId}
          onClose={() => dispatch(closeAddTaskModal())}
          lastTaskRank={lastTaskRank}
          tasksLength={tasks?.length}
          isAutoIncrementPaymentTitlesWsExtensionEnabled={
            isAutoIncrementPaymentTitlesExtensionEnabled
          }
        />
      )}

      {showAddPublicationModal && projectBasicData && (
        <AddPublicationModal
          projectId={projectBasicData.projectId}
          onClose={() => dispatch(closeAddPublicationModal())}
          lastTaskRank={lastTaskRank}
        />
      )}

      {showAddCreatorsModal && projectBasicData && (
        <AddCreatorsModal
          projectId={projectBasicData.projectId}
          onClose={() => dispatch(closeAddCreatorsModal())}
          lastTaskRank={lastTaskRank}
        />
      )}

      {showShareModal && (
        <ShareProjectOrTaskModal
          onClose={() => setShowShareModal(false)}
          data={projectBasicData}
          addMemberUrl={API_URLS.addProjectMembers}
          removeMemberUrl={API_URLS.removeProjectMember}
          variableName="wsProjectUuid"
          updateFunction={() => dispatch(getProject(projectId))}
        />
      )}

      {showAddTaskFieldModal && (
        <ManageFieldModal
          onClose={() => setShowAddTaskFieldModal(false)}
          dataType={tableDataType.Task}
          title={
            <IDHFormattedMessage
              id="ws_add_table_column"
              defaultMessage="Add table column"
            />
          }
          inputLabelText={
            <IDHFormattedMessage
              id="ws_column_title"
              defaultMessage="Column title"
            />
          }
          projectId={projectId}
        />
      )}

      {showAccessLinkModal && (
        <ManageFieldModal
          onClose={() => setShowAccessLinkModal(false)}
          dataType={tableDataType.Task}
          title={
            <IDHFormattedMessage
              id="ws_add_table_column"
              defaultMessage="Add table column"
            />
          }
          inputLabelText={
            <IDHFormattedMessage
              id="ws_column_title"
              defaultMessage="Column title"
            />
          }
          defaultOption={MetaFieldType.AccessLink}
          defaultFieldName={`${capitalizeFirstLetter(taskType)} Form`}
          hidden
          projectId={projectId}
        />
      )}

      {showTaskFieldSetupModal && (
        <ManageFieldModal
          data={editedFieldData}
          onClose={() => setShowTaskFieldSetupModal(false)}
          dataType={tableDataType.Task}
          projectId={projectId}
        />
      )}

      {showDeleteMetaFieldModal && (
        <DeleteMetaFieldModal
          uuid={editedFieldData.id}
          fieldName={editedFieldData.name}
          onClose={() => setShowDeleteMetaFieldModal(false)}
        />
      )}

      {howItWorksModalData.location === AppLocation.SingleProject && (
        <HowItWorksModal
          showTransition
          onClose={() => {
            removePropertyFromViewParam(history, "howItWorks");
            openSettingsModal(
              history,
              AppLocation.SingleProject,
              SettingsContext.Project,
              SettingsTab.Extensions,
              ExtensionsSection.RecruitmentForm,
            );
          }}
        />
      )}

      {isCreateSingleSplitPaymentsModalOpen && (
        <CreatePaymentModal
          onClose={() => dispatch(setCreateSingleSplitModalOpen(false))}
        />
      )}

      <Route
        path="/workspace/:workspaceUuid/projects/:projectId/:taskId/:commentId?"
        component={TaskPane}
      />

      <TaskTablePanes />

      {showSaveTableViewModal && (
        <SaveTableViewModal
          onClose={() => setShowSaveTableViewModal(false)}
          name={newTableViewName}
          setName={setNewTableViewName}
          saveFunction={saveTableView}
        />
      )}
    </>
  );
}
